import React from 'react';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Button } from '@/components/ui/button';
import { Play, Pause, Volume2, Loader2 } from 'lucide-react';
import { QuestionnaireData } from '@/types/category';
import { useBackgroundMusicOptions, getBackgroundMusicUrl } from '@/hooks/useBackgroundMusic';
import { useAudioPreview } from '@/hooks/useAudioPreview';
import { AudioTest } from '@/components/AudioTest';

interface BackgroundMusicSelectionProps {
  formData: QuestionnaireData;
  setFormData: (data: QuestionnaireData) => void;
}

const BackgroundMusicSelection: React.FC<BackgroundMusicSelectionProps> = ({
  formData,
  setFormData
}) => {
  // Fetch background music options from backend API
  const { options: musicOptions, isLoading, error } = useBackgroundMusicOptions();

  // Use the unified audio preview hook
  const { isPlaying, isLoading: isLoadingPreview, currentPreview, playPreview, stopPreview } = useAudioPreview();

  const handlePreviewToggle = async (musicFile: string) => {
    try {
      const audioUrl = getBackgroundMusicUrl(musicFile);
      await playPreview(audioUrl, musicFile);
    } catch (error) {
      console.error('Failed to preview audio:', error);
    }
  };

  const handleSelectionChange = (value: string) => {
    console.log('🎵 Background music selection changed:', value);
    console.log('🎵 Current formData.backgroundMusicSelection:', formData.backgroundMusicSelection);
    // If "none" is selected, set to null or empty string for backend
    const finalValue = value === 'none' ? null : value;
    setFormData({ ...formData, backgroundMusicSelection: finalValue as string });
    console.log('🎵 Updated formData:', { ...formData, backgroundMusicSelection: finalValue });
  };

  // Handle loading and error states
  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 animate-spin mr-2" />
        <span className="text-muted-foreground">Loading background music options...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 border border-destructive rounded-lg">
        <p className="text-destructive font-medium">Failed to load background music options</p>
        <p className="text-sm text-muted-foreground mt-1">{error.message}</p>
      </div>
    );
  }

  // Note: Even if musicOptions.length === 0, we still show the "None" option below

  return (
    <div>
      <Label className="text-base font-medium mb-4 block">
        <Volume2 className="inline-block w-5 h-5 mr-2" />
        Choose your background music
      </Label>
      <p className="text-sm text-muted-foreground mb-4">
        Select the background music that will play softly during your meditation session, or choose "None" for silence.
      </p>

      {/* Audio test component for debugging */}
      {process.env.NODE_ENV === 'development' && (
        <div className="mb-4">
          <AudioTest />
        </div>
      )}

      <RadioGroup
        value={formData.backgroundMusicSelection === null ? 'none' : formData.backgroundMusicSelection || ''}
        onValueChange={handleSelectionChange}
        className="space-y-3"
      >
        {/* None option */}
        <div className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50">
          <div className="flex items-center space-x-3 flex-1">
            <RadioGroupItem value="none" id="none" />
            <div className="flex-1">
              <Label htmlFor="none" className="cursor-pointer font-medium">
                None
              </Label>
              <p className="text-sm text-muted-foreground">
                No background music - silence only
              </p>
            </div>
          </div>
        </div>

        {musicOptions.map((option) => (
          <div key={option.filename} className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50">
            <div className="flex items-center space-x-3 flex-1">
              <RadioGroupItem value={option.filename} id={option.filename} />
              <div className="flex-1">
                <Label htmlFor={option.filename} className="cursor-pointer font-medium">
                  {option.name}
                </Label>
                <p className="text-sm text-muted-foreground">
                  {option.description}
                </p>
              </div>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => handlePreviewToggle(option.filename)}
              className="ml-4"
              disabled={currentPreview === option.filename && isLoadingPreview}
            >
              {currentPreview === option.filename && isLoadingPreview ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  Loading
                </>
              ) : currentPreview === option.filename && isPlaying ? (
                <>
                  <Pause className="w-4 h-4 mr-1" />
                  Stop
                </>
              ) : (
                <>
                  <Play className="w-4 h-4 mr-1" />
                  Preview
                </>
              )}
            </Button>
          </div>
        ))}
      </RadioGroup>
    </div>
  );
};

export default BackgroundMusicSelection;